import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSimilarContacts } from '../../hooks/queries/useContacts';
import type {
  SimilarContactCandidate,
  SimilarContactInput,
} from '../../services/contacts/contactService';

interface Props {
  input: SimilarContactInput;
  /** Si fourni, propose "Utiliser ce contact" qui appelle ce callback (cas création inline) */
  onPickExisting?: (contactId: string) => void;
}

const REASON_LABEL: Record<SimilarContactCandidate['match_reason'], string> = {
  email: 'même email',
  siret: 'même SIRET',
  phone: 'même téléphone',
  'name+cp': 'nom similaire, même code postal',
};

function formatCandidateName(c: SimilarContactCandidate): string {
  if (c.kind === 'corporate') return c.company_name || 'Société sans nom';
  return [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Sans nom';
}

export function SimilarContactsBanner({ input, onPickExisting }: Props) {
  const { data: candidates = [], isFetching } = useSimilarContacts(input);

  if (isFetching && candidates.length === 0) return null;
  if (candidates.length === 0) return null;

  const isPlural = candidates.length > 1;

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-amber-900">
            {candidates.length} contact{isPlural ? 's' : ''} similaire{isPlural ? 's' : ''} déjà
            au répertoire
          </p>
          <ul className="mt-1.5 space-y-1">
            {candidates.map((c) => (
              <li key={c.id} className="flex items-center gap-2 text-xs">
                <span className="flex-1 min-w-0 truncate text-amber-900">
                  <span className="font-medium">{formatCandidateName(c)}</span>
                  {c.email && <span className="text-amber-700"> · {c.email}</span>}
                  {c.city && <span className="text-amber-700"> · {c.city}</span>}
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-600">
                    {REASON_LABEL[c.match_reason]}
                  </span>
                </span>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {onPickExisting && (
                    <button
                      type="button"
                      onClick={() => onPickExisting(c.id)}
                      className="text-amber-700 hover:text-amber-900 font-medium underline-offset-2 hover:underline"
                    >
                      Utiliser
                    </button>
                  )}
                  <Link
                    to={`/contacts/${c.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 text-amber-700 hover:text-amber-900"
                    title="Voir le contact"
                  >
                    Voir <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
